// Kontrakt API `/api/agent-sync` — doc/robot/AGENT_SYNC_F1.md §3 + zmiany F2
// (doc/robot/AGENT_SYNC_F2.md §2a: strona A, konektor, stanowisko, obciążenie; §2b: statusy
// wiodące A↔T — D39 — i archiwum — D40). Pola camelCase,
// daty `YYYY-MM-DD`, czasy ISO-8601. Sumy przychodza jako dokladny tekst
// `numeric` (bez f64), liczniki jako liczby calkowite.

// Lista tabel jest data-driven (D44): przychodzi w odpowiedzi karty (`tables`, kolejność
// rejestru `TableSpec` w backendzie) — front nie trzyma własnej listy nazw.

/** Oś tabeli (D44): `day` = data biznesowa wiersza, `snapshot` = nocne zdjęcie stanu. */
export type SyncTableAxis = "day" | "snapshot";

export interface SyncTableInfo {
	name: string;
	axis: SyncTableAxis;
	/** D71: od kiedy Robot synchronizuje tabelę (`dgakt`, D49); brak = backend sprzed D71. */
	syncFrom?: string;
	/** D75: `false` = tabela bez strony A per apteka (`hurt`) — bez listy kluczy; brak = backend sprzed D75. */
	sideA?: boolean;
}

/** Kolumna treści wpisu katalogu (D46); `pii` nie trafia do logów. */
export interface CatalogColumn {
	name: string;
	kind: string;
	pii: boolean;
}

/** Wpis `GET /agent-sync/catalog` (D46): aktywny, zwalidowany, w kolejności rejestru. */
export interface CatalogEntry {
	table: string;
	level: number;
	version: number;
	axis: SyncTableAxis;
	/** D47: kadencja z rejestru (`hourly` = oś dnia, `nightly` = snapshot); brak → z `axis`. */
	cadence?: CatalogCadence;
	keyColumn: string | null;
	dateColumn: string | null;
	columns: CatalogColumn[];
	/** Poziom 1: wyrażenia SQL metryk; poziom 2: `[]`. */
	metrics: { name: string; expr: string }[];
	filterExpr: string | null;
	/** D48: filtr bazowy wiersza z rejestru (= `source_row_filter` Robota, np. `paca`: `tpodb = 2`); backend sprzed D48 go nie wysyła. */
	rowFilter?: string | null;
}

export type CatalogCadence = "hourly" | "nightly";

/** Preset kadencji stanowiska (D47): co godzinę + noc / co 4 h + noc / tylko noc. */
export const CADENCE_PRESETS = ["default", "slow", "nightly_only"] as const;
export type CadencePreset = (typeof CADENCE_PRESETS)[number];

/** Okno zakazane w czasie lokalnym apteki `HH:MM`; przez północ dozwolone. */
export interface ForbiddenWindow {
	from: string;
	to: string;
}

/** Ustawienia stanowiska (D47, `agent_sync_bindings`); `forbiddenWindow = null` = domyślne 02:00–04:00. */
export interface SyncSettings {
	paused: boolean;
	cadencePreset: CadencePreset;
	forbiddenWindow: ForbiddenWindow | null;
}

/** Body `PATCH …/sync-settings` — pominięte pole = bez zmiany, `forbiddenWindow: null` = domyślne. */
export interface SyncSettingsInput {
	paused?: boolean;
	cadencePreset?: CadencePreset;
	forbiddenWindow?: ForbiddenWindow | null;
}

/**
 * Status pary stron per (tabela, dzien). `unreachable` zarezerwowane dla strony A (F2).
 * `archive` (D40) = dzień sprzed granicy archiwum ERP/Source: dane są tylko w Target,
 * poza licznikami rozjazdów i ostrzeżeń.
 */
export type SyncStatus = "ok" | "warn" | "mismatch" | "pending" | "n/a" | "unreachable" | "archive";

/**
 * Para wiodąca (D39): `at` = Apteka ↔ Target (apteka z włączonym stanowiskiem),
 * `st` = Source ↔ Target (bez stanowiska, jak w F1).
 */
export type LeadPair = "at" | "st";

/** Stan usługi sync na stanowisku apteki (`service_state` bindingu). */
export const SERVICE_STATES = [
	"disabled",
	"starting",
	"running",
	"stopped",
	"error",
	// D42: silnik lokalny (BDE/FM.xml) albo sonda ≠ konektor — usługa wstrzymuje joby.
	"engine_drift",
	// D47: usługa w pauzie (heartbeat dalej leci, jobów nie pobiera).
	"paused",
] as const;
export type ServiceState = (typeof SERVICE_STATES)[number];

/** `none` = apteka bez stanowiska sync (brak bindingu), inaczej stan usługi. */
export type AgentState = "none" | ServiceState | (string & {});

export type MetricValue = string | number | Record<string, unknown> | null;

export interface PharmacyTrendPoint {
	date: string;
	status: SyncStatus | null;
}

export interface PharmacyListRow {
	idfirm: number;
	name: string | null;
	wlasna: boolean | null;
	region: string | null;
	agentState: AgentState;
	engine: string | null;
	/** Wersja silnika z sondy usługi sync (np. `18.0.0.0.0`, `3.0.14`); `null` przed pierwszym połączeniem. */
	engineVersion: string | null;
	/** D42: ostatnia zmiana silnika ERP zgłoszona przez stanowisko (`POST /device/engine`). */
	engineChangedAt: string | null;
	engineChangedFrom: string | null;
	engineChangedTo: string | null;
	lastRunAt: string | null;
	lastRunStatus: string | null;
	lastRunError: string | null;
	/** Para wiodąca apteki (D39) — `at` tylko z włączonym stanowiskiem sync. */
	leadPair: LeadPair;
	/** Wiodące (D39): A↔T przy `leadPair = "at"`, inaczej S↔T. Bez dni `archive`. */
	worstStatus: SyncStatus | null;
	daysMismatch: number | null;
	daysWarn: number | null;
	daysMismatchSt: number | null;
	daysWarnSt: number | null;
	daysPendingSt: number | null;
	/** Dni z `status_as='mismatch'` (35 dni bez bieżącego); `null` bez bindingu. */
	daysMismatchAs: number | null;
	/**
	 * Dni z rozjazdem WIODĄCYM (D39) w oknie trendu (14 dni) — liczba słupków `trend`,
	 * jej filtra i sortu. Brak pola (odpowiedź sprzed D39) → `daysMismatch14St`.
	 */
	daysMismatch14?: number;
	/** D47: stanowisko wstrzymane (`paused` bindingu); brak pola = backend sprzed D47. */
	paused?: boolean;
	/** Dni z rozjazdem S↔T w oknie trendu (14 dni). */
	daysMismatch14St: number;
	worstStatusSt: SyncStatus | null;
	worstStatusAs: SyncStatus | null;
	/** Apteka ↔ Target (D39); `null` bez bindingu. */
	worstStatusAt: SyncStatus | null;
	daysMismatchAt: number | null;
	daysWarnAt: number | null;
	trend: PharmacyTrendPoint[];
	cursorLagMinutes: number | null;
	/** Mediana czasu zapytania ERP w ostatnim cyklu strony A. */
	loadMedianMs: number | null;
	backoffLevel: number | null;
	/** Miesiące całej historii z rozjazdem (przegląd historyczny, §5). */
	monthsMismatch: number;
	/** Miesiące zlecone farmaster-robot, jeszcze nieprzetworzone. */
	repairInProgress: number;
	/**
	 * Kolumna „Naprawa”: stan napraw apteki liczony tymi samymi regułami co ręczne
	 * zlecenie (D64). Brak = backend sprzed pola.
	 */
	repair?: RepairSummary;
}

/** Agregat napraw apteki (lista) — pozycje dialogu „Napraw rozjazdy” zliczone po stanie. */
export interface RepairSummary {
	/** Miesiące, które Robot przyjmie teraz („Napraw (n)”). */
	repairable: number;
	/** Czekające na Robota zlecenia ręczne. */
	queued: number;
	/** Naprawione przez Robota, czekają na ponowne sprawdzenie zgodności. */
	repaired: number;
	/** Rozjazdy, których Robot nie naprawi (powód w `blockReason`). */
	blocked: number;
	/** Najczęstszy powód blokady. */
	blockReason: RepairBlockReason | null;
}

export interface PharmacyListSummary {
	pharmacies: number;
	withMismatch: number;
	withWarn: number;
	lastHourlyAt: string | null;
	lastNightlyAt: string | null;
}

export interface PharmacyListResponse {
	rows: PharmacyListRow[];
	total: number;
	page: number;
	pageSize: number;
	summary: PharmacyListSummary;
}

/**
 * Kolumny sortowalne po stronie serwera (whitelist `sortBy`: F1 §3.1 + F2 §2a/§2b) —
 * każda kolumna listy.
 */
export const PHARMACY_SORT_COLUMNS = [
	"idfirm",
	"name",
	"region",
	"agentState",
	"daysMismatchSt",
	"daysWarnSt",
	"monthsMismatch",
	"repairInProgress",
	"repair",
	"lastRunAt",
	"worstStatusSt",
	"trend",
	"cursorLagMinutes",
	// F2: każda kolumna F2 ma sort (zasada „żadnej kolumny bez sortu i filtra”).
	"daysMismatchAs",
	"loadMedianMs",
	"engine",
	"worstStatusAs",
	"backoffLevel",
	// D39: statusy i liczniki wiodące oraz para A↔T.
	"worstStatus",
	"daysMismatch",
	"daysWarn",
	"worstStatusAt",
	"daysMismatchAt",
	"daysWarnAt",
	// D42: data ostatniej zmiany silnika ERP.
	"engineChangedAt",
	// D47: stanowisko wstrzymane.
	"paused",
] as const;
export type PharmacySortColumn = (typeof PHARMACY_SORT_COLUMNS)[number];

export interface PharmacyListParams {
	scope: string;
	sortBy?: PharmacySortColumn;
	sortDir?: "asc" | "desc";
	columnFilters?: string;
}

export interface SideCounts {
	count: number | null;
	countAll: number | null;
	metrics: Record<string, MetricValue>;
}

export interface MetricDiff {
	s: MetricValue;
	t: MetricValue;
	delta: MetricValue;
}

/**
 * D72 `diff.archive`: wiersze, które Source i Target mają tylko dlatego, że apteka
 * obcięła historię w ERP (`rows` = wariant `filtered`, `rowsAll` = wszystkie);
 * `cut` = pierwszy dzień bieżących danych apteki. Poza porównaniem i licznikami.
 */
export interface PharmacyArchiveDiff {
	rows: number;
	rowsAll: number;
	cut: string;
}

/**
 * Różnice per wariant (`filtered`/`all`) — tylko różne metryki — i obok nich
 * (D72) `archive`, który NIE jest wariantem: iterować wyłącznie po wariantach.
 */
export interface VariantDiffs<M> {
	filtered?: Record<string, M>;
	all?: Record<string, M>;
	archive?: PharmacyArchiveDiff;
}

/** `{ "<wariant>": { "<metryka>": { s, t, delta } } }` — tylko rozne metryki. */
export type SyncDiff = VariantDiffs<MetricDiff>;

export interface MetricDiffAs {
	a: MetricValue;
	s: MetricValue;
	delta: MetricValue;
}

/** Różnice Apteka ↔ Source: `{ "<wariant>": { "<metryka>": { a, s, delta } } }`. */
export type SyncDiffAs = VariantDiffs<MetricDiffAs>;

export interface MetricDiffAt {
	a: MetricValue;
	t: MetricValue;
	delta: MetricValue;
}

/** Różnice Apteka ↔ Target (D39): `{ "<wariant>": { "<metryka>": { a, t, delta = a − t } } }`. */
export type SyncDiffAt = VariantDiffs<MetricDiffAt>;

/** D72: wpis archiwum apteki tabeli w karcie (`pharmacyArchive`). */
export interface PharmacyArchiveEntry {
	/** Pierwszy dzień bieżących danych apteki `YYYY-MM-DD`; wiersze sprzed niego to archiwum. */
	cut: string;
	/** Wiersze archiwum (wariant `filtered`). */
	rows: number;
	/** Wiersze archiwum z buforowymi i usuniętymi. */
	rowsAll: number;
	/** Liczba miesięcy archiwum (tabele z osią dnia); brak przy snapshocie. */
	months?: number;
	measuredAt: string;
}

export interface DayCell {
	/** Status wiodący (D39): `COALESCE(status_at, status_st)` z włączonym stanowiskiem, inaczej `status_st`. */
	status: SyncStatus;
	statusSt: SyncStatus;
	statusAs: SyncStatus | null;
	statusAt: SyncStatus | null;
	diagnosis: string | null;
	a: SideCounts | null;
	s: SideCounts | null;
	t: SideCounts | null;
	diff: SyncDiff | null;
	diffAs: SyncDiffAs | null;
	diffAt: SyncDiffAt | null;
	keysAvailable: boolean;
}

/** Granica archiwum ERP tabeli (D40): dni `< from` są tylko w Target. */
export interface ArchiveBound {
	from: string;
	measuredAt: string | null;
}

/**
 * Komórka karty apteki w trybie `compact` (panel — kafle dróg): same statusy dnia.
 * Pełną komórkę (strony, różnice) daje `GET …/days`.
 */
export type DayStatusCell = Pick<DayCell, "status" | "statusSt" | "statusAs" | "statusAt">;

export interface PharmacyDay {
	date: string;
	/** Komorka bez wiersza statusu jest pominieta — UI pokazuje „—”. */
	cells: Partial<Record<string, DayStatusCell>>;
}

export interface CursorLag {
	table: string;
	maxDgaktS: string | null;
	maxDgaktT: string | null;
	/**
	 * Zaległość Robota: ile najstarsza zmiana po kursorze Robota czekała w chwili
	 * pomiaru (od pierwszego pomiaru, który ją zobaczył; dolne ograniczenie). 0 bez
	 * zmian w ruchu, `null` bez kursora Robota. Nie `maxDgaktS − maxDgaktT`.
	 */
	lagMinutes: number | null;
	/** Wiersze Source po kursorze Robota w oknie pomiaru. */
	inFlightRows?: number | null;
	waitingSince?: string | null;
	measuredAt?: string | null;
}

export interface PharmacyKpi {
	/** Wiodące (D39): A↔T przy stanowisku, inaczej S↔T; bez dni `archive`. */
	daysMismatch: number;
	daysWarn: number;
	daysMismatchAt: number | null;
	daysWarnAt: number | null;
	/** Dni zgodne A↔T / A↔S (`null` bez stanowiska) — bez porównań kafel nie mówi „zgodne”. */
	daysOkAt: number | null;
	daysOkAs: number | null;
	daysMismatchSt: number;
	daysWarnSt: number;
	daysOkSt: number;
	daysMismatchAs: number | null;
	daysWarnAs: number | null;
	/** Ostatni przebieg strony A (job `level1` stanowiska). */
	lastARunAt: string | null;
	lastARunStatus: string | null;
	lastHourlyAt: string | null;
	lastNightlyAt: string | null;
	lastRunStatus: string | null;
	lastRunError: string | null;
}

/** Źródło poświadczeń ERP: wpisane w panelu, import `FM.xml` albo formularz helpera. */
export type CredentialsSource = "panel" | "fm_xml" | "helper" | (string & {});

/** Konektor ERP apteki — hasło nigdy nie przychodzi, tylko `hasPassword`. */
export interface ConnectorInfo {
	dbType: string | null;
	host: string | null;
	port: number | null;
	database: string | null;
	username: string | null;
	hasPassword: boolean;
	credentialsSource: CredentialsSource | null;
	/** Id użytkownika, który ustawił poświadczenia (`null` = FM.xml / helper). */
	credentialsSetBy: number | string | null;
	/** Nazwa tego użytkownika (backend dołącza); brak = „użytkownik #<id>”. */
	credentialsSetByName?: string | null;
	credentialsSetAt: string | null;
	awaitingCredentials: boolean;
	engineDetected: string | null;
	engineVersionDetected: string | null;
	fingerprintAt: string | null;
	lastTestAt: string | null;
	lastTestOk: boolean | null;
	lastTestError: string | null;
	/** D42: ostatnia zmiana silnika ERP (`POST /device/engine`). */
	engineChangedAt: string | null;
	engineChangedFrom: string | null;
	engineChangedTo: string | null;
	/** D42: silnik lokalny z heartbeatu stanowiska; `null` = brak heartbeatu z tym polem. */
	localEngine: LocalEngine | null;
	/** D72: archiwum apteki per tabela (to samo co `pharmacyArchive` karty); brak = backend sprzed D72. */
	pharmacyArchive?: Record<string, PharmacyArchiveEntry>;
}

/** Silnik z lokalnych źródeł stanowiska: alias BDE `KS-APW` i `FM.xml` `TYPE` (D42). */
export interface LocalEngine {
	bde: string | null;
	fmXml: string | null;
}

/** Body `PUT …/connector`: `password` pominięte = bez zmiany, `""` = usuń. */
export interface ConnectorInput {
	dbType: string;
	host: string;
	port: number;
	database: string;
	username: string;
	password?: string;
}

/**
 * Odpowiedź `PUT …/connector` („connector jak w karcie + pushed”). Panel czyta
 * tylko `pushed`, a konektor bierze z ponownie pobranej karty — działa więc
 * zarówno dla `{connector, pushed}`, jak i płaskiego kształtu.
 */
export interface ConnectorSaveResponse {
	connector?: ConnectorInfo | null;
	/** Czy zapis wysłano do stanowiska z włączonym bindingiem (online). */
	pushed: boolean;
}

/** Stanowisko sync apteki (jedno aktywne na idfirm). */
export interface BindingInfo {
	deviceId: string;
	deviceName: string | null;
	deviceOnline: boolean;
	enabled: boolean;
	enabledAt: string | null;
	serviceState: AgentState | null;
	serviceVersion: string | null;
	/** Metadane builda usługi sync (`g<sha7> <data> <x86|x64> <sterowniki>`), tylko diagnoza. */
	buildInfo?: string | null;
	lastSeenAt: string | null;
	lastError: string | null;
	backoffLevel: number | null;
}

/** Urządzenie apteki do wyboru stanowiska. `syncCapable` = agent ≥ 0.1.119. */
export interface PharmacyDevice {
	deviceId: string;
	name: string | null;
	online: boolean;
	serviceVersion: string | null;
	/** Metadane builda agenta (`g<sha7> <data> <x86|x64>`). */
	buildInfo?: string | null;
	syncCapable: boolean;
}

export interface LoadCycle {
	runId: number;
	at: string;
	queries: number | null;
	medianMs: number | null;
	p95Ms: number | null;
	maxMs: number | null;
	rows: number | null;
	bytes: number | null;
	cpuMs: number | null;
	rssBytes: number | null;
	/** D76: werdykt backoffu (liczy backend); `null` = cykl poza backoffem (nocny, ręczny, bez zapytań), brak = backend sprzed D76. */
	overNorm?: boolean | null;
}

export interface LoadInfo {
	baselineMedianMs: number | null;
	backoffLevel: number | null;
	/** Ostatnie 48 cykli, najnowszy pierwszy. */
	cycles: LoadCycle[];
}

export interface PharmacyDetailResponse {
	pharmacy: { idfirm: number; name: string | null; wlasna: boolean | null; region: string | null };
	agentState: AgentState;
	leadPair: LeadPair;
	/** Granice archiwum ERP per tabela (D40); `{}` bez pomiaru. */
	archiveBounds: Record<string, ArchiveBound>;
	/**
	 * D72: archiwum apteki per tabela — wiersze, które Source i Target mają tylko
	 * dlatego, że apteka obcięła historię w ERP; `{}` bez archiwum, brak = backend sprzed D72.
	 */
	pharmacyArchive?: Record<string, PharmacyArchiveEntry>;
	/** `null` = apteka bez konektora ERP. Hasło nigdy nie przychodzi. */
	connector: ConnectorInfo | null;
	/** `null` = apteka bez stanowiska sync. */
	binding: BindingInfo | null;
	devices: PharmacyDevice[];
	load: LoadInfo | null;
	/** D47: ustawienia stanowiska; `null` bez bindingu, brak pola = backend sprzed D47. */
	settings?: SyncSettings | null;
	kpi: PharmacyKpi;
	cursor: CursorLag[];
	sourceCostMs: Record<string, number> | null;
	/** Tabele siatki w kolejności rejestru `TableSpec` (D44) — jedyna lista tabel frontu. */
	tables: SyncTableInfo[];
	/** Najnowszy pierwszy; komórki z samymi statusami (`compact`). */
	days: PharmacyDay[];
	/** D71: historia strony A porcjami rocznymi; `null` bez stanowiska, brak = backend sprzed D71. */
	aHistory?: AHistoryInfo | null;
	/** D71: miejsce apteki w kolejce sprawdzeń Source ↔ Target; `null` = nie czeka. */
	stQueue?: PharmacyStQueue | null;
}

/** D71: plan historii strony A apteki — porcje roczne liczone po kolei. */
export interface AHistoryPlan {
	id: number;
	status: "running" | "done" | "cancelled" | "error";
	/**
	 * D104: `auto` = automatyczny pomiar jednego miesiąca z naprawą wstrzymaną (D38).
	 * D106: `initial` = pełna historia założona przez backend po podłączeniu apteki.
	 */
	source: "manual" | "auto" | "initial";
	tables: string[];
	windowFrom: string;
	windowTo: string;
	chunksTotal: number;
	chunksDone: number;
	/** Bieżąca porcja; `null` po zakończeniu planu. */
	chunkFrom: string | null;
	chunkTo: string | null;
	/** Job bieżącej porcji; `null` = porcja czeka na wolną chwilę stanowiska. */
	jobStatus: JobStatus | null;
	/** Nieudane próby bieżącej porcji (najwyżej 3, potem plan kończy się błędem). */
	attempts: number;
	monthsWritten: number;
	error: string | null;
	followupCheckId: number | null;
	createdAt: string;
	finishedAt: string | null;
}

/** Data „od” tabeli w planie: synchronizacja Robota, granica archiwum ERP, pierwszy miesiąc. */
export interface AHistoryTableFrom {
	table: string;
	syncFrom: string;
	archiveFrom: string | null;
	/** `null` = tabela bez miesięcy do sprawdzenia (pominięta). */
	firstMonth: string | null;
}

export interface AHistoryPreview {
	windowFrom: string;
	windowTo: string;
	chunksTotal: number;
	tables: AHistoryTableFrom[];
}

export interface AHistoryInfo {
	active: AHistoryPlan | null;
	/** Ostatni zakończony plan (gotowy, anulowany albo z błędem). */
	last: AHistoryPlan | null;
	/** Plan „od początku”; `null` = brak miesięcy sprzed okna nocnego. */
	preview: AHistoryPreview | null;
	/** `dateFrom` zlecenia „od początku” (dolna granica przeglądu). */
	checkFrom: string;
}

/** Najstarsze zlecenie sprawdzenia S ↔ T, w którym apteka czeka albo jest liczona. */
export interface PharmacyStQueue {
	checkId: number;
	status: ManualCheckStatus;
	itemStatus: ManualItemStatus;
	dateFrom: string;
	dueOnly: boolean;
	/** Miejsce zlecenia wśród oczekujących (1 = następne); `null` dla trwającego. */
	position: number | null;
	waitingReason: ManualCheck["waitingReason"];
	waitingUntil: string | null;
}

/**
 * Status miesiąca: `archive` = przed granicą archiwum (D40) — Target legalnie
 * trzyma dane starsze niż Source/apteka; poza licznikami i naprawą.
 */
export type MonthStatus = "ok" | "warn" | "mismatch" | "n/a" | "archive";

/**
 * Stan naprawy miesiąca: zlecona robotowi / wykonana / poza horyzontem robota /
 * archiwum robota / robot nie widzi rozjazdu / tylko z danych apteki (D38/D39).
 */
export type RepairState =
	| "requested"
	| "repaired"
	| "no_drift"
	| "beyond_horizon"
	| "archiwum"
	| "pharmacy";

/** D64: `details.reason` 409 `month_not_repairable` — także `repairBlockReason` karty i dnia. */
export type RepairBlockReason =
	| "status"
	| "before_sync_from"
	| "pharmacy_source"
	| "archive_boundary"
	| "awaiting_pharmacy"
	| "robot_declined"
	| "not_reviewed"
	/** Apteka poza partycjami Robota — tylko podgląd, lista i zlecenie zbiorcze (D67). */
	| "robot_not_fetching";

/**
 * Stan miesiąca w dialogu „Napraw rozjazdy” (`GET /agent-sync/repairs/preview`):
 * do naprawy / zablokowany (`blockReason`) / w kolejce Robota / naprawiony, czeka
 * na kontrolę / zakończony.
 */
export type RepairItemState = "repairable" | "blocked" | "queued" | "repaired" | "done";

/** Krok ostatniego zlecenia: zlecona → odpowiedź Robota → sprawdzona (Robot nie raportuje „pracuje”). */
export type RepairStep = "requested" | "answered" | "checked";

export interface RepairItem {
	table: string;
	/** Pierwszy dzień miesiąca `YYYY-MM-01`. */
	month: string;
	/** `null` = miesiąc bez wiersza historii (zlecony z dnia). */
	status: MonthStatus | null;
	diagnosis: string | null;
	countS: number | null;
	countT: number | null;
	state: RepairItemState;
	blockReason: RepairBlockReason | null;
	/** Czeka zlecenie nocne — ręczne podniesie je (D64). */
	promotable: boolean;
	/** Krok zlecenia z ostatnich 7 dni; `null` = brak. */
	step: RepairStep | null;
	outcome: RepairOutcome | null;
	requestedAt: string | null;
	processedAt: string | null;
}

export interface RepairPreviewPharmacy {
	idfirm: number;
	name: string | null;
	wlasna: boolean;
	region: string | null;
	items: RepairItem[];
}

export interface RepairPreviewResponse {
	/** Apteki w zakresie i kontroli S↔T (wyłączone z Robota są pominięte), kolejność zapytania. */
	pharmacies: RepairPreviewPharmacy[];
	/** Robot: zleceń na przebieg i ręcznych zleceń organizacji czekających przed nowymi. */
	robot: { perPass: number; manualWaiting: number };
}

/** Pozycja `POST /agent-sync/repairs`. */
export interface RepairTarget {
	idfirm: number;
	table: string;
	/** Pierwszy dzień miesiąca `YYYY-MM-01`. */
	month: string;
}

export type RepairResultKind =
	| "requested"
	| "already_queued"
	| "not_repairable"
	| "pharmacy_unavailable"
	| "error";

export interface RepairResult extends RepairTarget {
	result: RepairResultKind;
	reason?: RepairBlockReason;
	/** Wyjaśnienie odmowy po polsku (backend). */
	message?: string;
	requestedAt?: string | null;
	requestCount?: number;
	/** Czekające zlecenie nocne podniesione do ręcznego. */
	promoted?: boolean;
	fromDay?: boolean;
}

export interface RepairsResponse {
	results: RepairResult[];
	summary: Partial<Record<RepairResultKind, number>>;
}

/** Wynik robota na zlecenie naprawy miesiąca (`reconciliation_backlog.outcome`). */
export type RepairOutcome = Extract<
	RepairState,
	"repaired" | "no_drift" | "beyond_horizon" | "archiwum"
>;

export type JobStatus = "queued" | "sent" | "running" | "done" | "failed" | "expired" | "cancelled";
export const TERMINAL_JOB_STATUSES: readonly JobStatus[] = [
	"done",
	"failed",
	"expired",
	"cancelled",
];

/** Wynik `test_connection` / `fingerprint` (bez sekretów). */
export interface TestConnectionResult {
	ok?: boolean;
	engine?: string | null;
	engineVersion?: string | null;
	charset?: string | null;
	ksid?: number | null;
	error?: string | null;
}

export interface AgentSyncJob {
	id: number;
	kind: string;
	status: JobStatus;
	createdAt: string;
	finishedAt: string | null;
	error: string | null;
	result: TestConnectionResult | Record<string, unknown> | null;
}

export interface JobAccepted {
	jobId: number;
}

/** `GET /device-bindings` — bindingi aptek widocznych dla użytkownika (kafelek urządzenia). */
export interface DeviceBindingRow {
	idfirm: number;
	deviceId: string;
	enabled: boolean;
	serviceState: AgentState | null;
	serviceVersion: string | null;
	buildInfo?: string | null;
	lastSeenAt: string | null;
	lastError: string | null;
}

// ── Masowe sprawdzenie zgodności (F1 §7) ────────────────────────────────────

export type ManualCheckStatus = "queued" | "running" | "done" | "cancelled";
export type ManualItemStatus = "pending" | "running" | "done" | "error" | "cancelled";

/**
 * Okno sprawdzeń ręcznych: `night` — pełna równoległość; `day` — godziny pracy
 * (pon–pt 07–16) w ramach wyjątku właściciela, niższa równoległość; `wait` —
 * godziny pracy po wygaśnięciu wyjątku, zlecenie czeka do `waitUntil`.
 */
export interface ManualCheckWindow {
	state: "night" | "day" | "wait";
	waitUntil: string | null;
	exceptionUntil: string;
	nightParallelism: number;
	dayParallelism: number;
}

export interface ManualCheckCounts {
	pending: number;
	running: number;
	done: number;
	error: number;
	cancelled: number;
}

export interface ManualCheck {
	id: number;
	status: ManualCheckStatus;
	tables: string[];
	/** Przy `dueOnly` — dzień zlecenia (zaległe kontrole nie mają daty „od”). */
	dateFrom: string;
	dueOnly: boolean;
	requestedBy: number | null;
	requestedByName: string | null;
	mergedCount: number;
	createdAt: string;
	startedAt: string | null;
	finishedAt: string | null;
	cancelRequestedAt: string | null;
	parallelism: number | null;
	/** `business_hours` — czeka do `waitingUntil`; `nightly` — wstrzymane na czas nocy. */
	waitingReason: "business_hours" | "nightly" | null;
	waitingUntil: string | null;
	queuePosition: number | null;
	/** Tylko apteki z zakresu i widoczności użytkownika. */
	counts: ManualCheckCounts;
}

export interface ManualChecksResponse {
	checks: ManualCheck[];
	window: ManualCheckWindow;
}

export interface ManualCheckItem {
	idfirm: number;
	name: string | null;
	status: ManualItemStatus;
	startedAt: string | null;
	finishedAt: string | null;
	error: string | null;
}

/** Średni czas zapytań S+T na aptekę dla tabeli (ms) i średnia liczba miesięcy przeglądu. */
export interface ManualCostTable {
	ms: number;
	months: number | null;
}

export interface ManualCostKind {
	tables: Record<string, ManualCostTable>;
	queryMs: number;
	wallMs: number | null;
	runs: number;
}

/** `GET /manual-checks/cost` — pomiary `agent_sync_runs` z 3 dni; szacunek liczy panel. */
export interface ManualCheckCost {
	basis: { nightly: ManualCostKind; history: ManualCostKind };
	tables: SyncTableInfo[];
	nightlyWindowDays: number;
	historyFloor: string;
	window: ManualCheckWindow;
}

export interface ManualCheckInput {
	tables: string[];
	/** Wymagana, chyba że `due` (zaległe kontrole liczą się bez daty „od”). */
	dateFrom?: string;
	/**
	 * Zaległe kontrole: najpierw odpowiedzi Robota, potem tylko miesiące z minionym
	 * terminem kontroli — bez dni i bez strony APTEKA.
	 */
	due?: boolean;
	/**
	 * `all` — wszystkie apteki zakresu; `filtered` — według bieżących filtrów listy;
	 * `one` — apteka `idfirm` (karta, D71).
	 */
	pharmacies: "all" | "filtered" | "one";
	idfirm?: number;
	columnFilters?: string;
}

/** `POST /manual-checks/due` — podgląd zaległych kontroli (bez zapisu). */
export interface ManualCheckDue {
	months: number;
	pharmacies: number;
	oldest: string | null;
	byTable: Record<string, number>;
}

export interface ManualCheckAccepted {
	id: number;
	merged: boolean;
	pharmacies: number;
	aJobs: number;
	aJobErrors: number;
	/** D71: plany historii A (data „od” sprzed okna nocnego). */
	aHistory?: number;
}

// ── D72: lista kluczy „Z czego wynika rozjazd?” ──────────────────────────────

/** Klasa klucza różniącego się między A, S i T; `archive` to nie rozjazd. */
export const KEY_CLASSES = [
	"archive",
	"erp_deleted",
	"erp_missing",
	"erp_changed",
	"robot_missing",
	"robot_orphan",
	"robot_changed",
	"source_only",
	"source_missing",
	"all_differ",
	"measure",
] as const;
export type KeyClass = (typeof KEY_CLASSES)[number];

export type KeyCheckStatus =
	| "queued"
	| "sent"
	| "computing"
	| "done"
	| "error"
	| "cancelled"
	/** Automat pominął miesiąc (np. ponad limit wierszy) — powód w `error`. */
	| "skipped";

/** `manual` — zlecona z panelu, `auto` — daemon sam zleca dla rozjazdów A↔T. */
export type KeyCheckSource = "manual" | "auto";

/** Liczba kluczy strony: wszystkie i bez buforowych/usuniętych. */
export interface KeyCheckSideCount {
	rows: number;
	filtered: number;
}

export interface KeyCheckSummary {
	a: KeyCheckSideCount;
	s: KeyCheckSideCount;
	t: KeyCheckSideCount;
	/** Klucze zgodne po trzech stronach. */
	equal: number;
	classes: Partial<Record<KeyClass, number>>;
	/** Lista kluczy przycięta (backend zapisuje najwyżej 2 000 pozycji). */
	truncated: boolean;
	/** Granica archiwum apteki tabeli (D72) albo `null`. */
	cut: string | null;
	sMs: number;
	tMs: number;
}

export interface KeyCheck {
	id: number;
	idfirm: number;
	table: string;
	dateFrom: string;
	dateTo: string;
	status: KeyCheckStatus;
	jobId: number | null;
	error: string | null;
	/** Czas zapytania na ERP apteki (ms). */
	aMs: number | null;
	summary: KeyCheckSummary | null;
	requestedBy: number | null;
	source: KeyCheckSource;
	/** F3: lista porównuje też treść wiersza (hash A/S/T). */
	content?: boolean;
	createdAt: string;
	aReceivedAt: string | null;
	finishedAt: string | null;
}

/** Sumy różniące się na kluczu: `{ suma: { a, s, t } }` (dokładny tekst `numeric`). */
export type KeyItemSums = Record<string, { a: string | null; s: string | null; t: string | null }>;

export interface KeyItem {
	key: string;
	aDate: string | null;
	sDate: string | null;
	tDate: string | null;
	/** `true` = wiersz bieżący (wskus = 0, bufor = 0); `false` = buforowy/usunięty; `null` = brak strony. */
	aFiltered: boolean | null;
	sFiltered: boolean | null;
	tFiltered: boolean | null;
	class: KeyClass;
	reason: string;
	sums: KeyItemSums | null;
	sRow: Record<string, unknown> | null;
	tRow: Record<string, unknown> | null;
	/** F3: hash tekstu kanonicznego wiersza (16 znaków hex); `null` = brak strony albo treści. */
	aHash?: string | null;
	sHash?: string | null;
	tHash?: string | null;
	/** F3: kolumny o różnej treści; pusta = nieznane (poziom 3 jeszcze nie policzony). */
	changedColumns?: string[];
}

/** F3 poziom 3: wartości kolumn klucza po stronach (`null` = brak wiersza albo strony). */
export interface Level3Row {
	key: string;
	a: string[] | null;
	s: string[] | null;
	t: string[] | null;
	/** Kolumny, w których obecne strony się różnią. */
	differs: string[];
	diagnosis: string;
}

export type Level3Status = "queued" | "sent" | "computing" | "done" | "error" | "cancelled";

/** F3 poziom 3: wartości `content_columns` kluczy z trzech stron (bez PII, D46), TTL 24 h. */
export interface Level3 {
	id: number;
	table: string;
	checkId: number | null;
	source: KeyCheckSource;
	status: Level3Status;
	error: string | null;
	dateFrom: string;
	dateTo: string;
	columns: string[];
	rows: Level3Row[];
	createdAt: string;
	finishedAt: string | null;
	expiresAt: string;
}

export interface Level3Input {
	table: string;
	keys: number[];
	dateFrom: string;
	dateTo: string;
	checkId?: number;
}

export interface KeyCheckInput {
	table: string;
	dateFrom: string;
	dateTo: string;
}

/** Zakres dni `YYYY-MM-DD` (obie granice włącznie). */
export type KeyCheckRange = Pick<KeyCheckInput, "dateFrom" | "dateTo">;

export interface KeyCheckDetail {
	check: KeyCheck;
	items: KeyItem[];
}

/** SQL weryfikacji rozjazdu: karta miesiąca (`month`) albo panel dnia (`day`). */
export type VerifySqlKind = "month" | "day";

/** Skrypt jednej strony: A = baza ERP apteki, S = Source, T = Target. */
export interface VerifySqlSide {
	side: "A" | "S" | "T";
	/** Silnik bez danych dostępowych; `null` = brak konektora apteki. */
	engine: "oracle" | "firebird" | "postgresql" | null;
	sql: string | null;
	/** Powód braku skryptu albo uwaga do niego. */
	note: string | null;
}

export interface VerifySqlResponse {
	table: string;
	kind: VerifySqlKind;
	dateFrom: string;
	dateTo: string;
	/** Najnowsza gotowa lista kluczy D72 nachodząca na zakres (część 3 skryptów). */
	keyCheck: { id: number; dateFrom: string; dateTo: string; keys: number } | null;
	sides: VerifySqlSide[];
}
